import React, { useState } from 'react';
import { useModules, MODULE_CATALOGUE, METHODOLOGY_PACKS } from '../../context/ModuleContext';
import { Card } from '../../design-system/Card';
import { Button } from '../../design-system/Button';
import { Badge } from '../../design-system/Badge';
import { Input } from '../../design-system/Input';
import { Tabs } from '../../design-system/Tabs';
import { Modal } from '../../design-system/Modal';
import {
  Compass,
  Search,
  Check,
  Plus,
  Sparkles,
  Layers,
  ShieldCheck,
  ExternalLink,
  Sliders
} from 'lucide-react';

export const ModuleLibraryPage = ({ setCurrentView, setCurrentProjectView }) => {
  const { enabledModules, toggleModule, applyMethodology, activeMethodology } = useModules();
  const [activeTab, setActiveTab] = useState('modules'); // 'modules' or 'methodologies'
  const [search, setSearch] = useState('');
  const [methodologyCategory, setMethodologyCategory] = useState('all');

  // Methodology Customizer Modal State
  const [customizingPack, setCustomizingPack] = useState(null);
  const [customSelectedModules, setCustomSelectedModules] = useState([]);

  const libraryTabs = [
    { id: 'modules', label: `All Modules (${MODULE_CATALOGUE.length})`, icon: <Compass size={15} /> },
    { id: 'methodologies', label: `Methodology Packs (${METHODOLOGY_PACKS.length})`, icon: <Layers size={15} /> }
  ];

  const filteredModules = MODULE_CATALOGUE.filter((m) =>
    m.name.toLowerCase().includes(search.toLowerCase()) ||
    m.description.toLowerCase().includes(search.toLowerCase()) ||
    m.category.toLowerCase().includes(search.toLowerCase())
  );

  const filteredMethodologies = METHODOLOGY_PACKS.filter((p) => {
    if (methodologyCategory === 'all') return true;
    return p.category.toLowerCase() === methodologyCategory.toLowerCase();
  });

  const handleLaunchModule = (moduleId) => {
    if (setCurrentView && setCurrentProjectView) {
      setCurrentView('project_view');
      setCurrentProjectView(moduleId);
    }
  };

  const handleOpenCustomize = (pack) => {
    setCustomizingPack(pack);
    setCustomSelectedModules([...pack.recommendedModules]);
  };

  const handleToggleCustomModule = (modId) => {
    setCustomSelectedModules((prev) =>
      prev.includes(modId) ? prev.filter((id) => id !== modId) : [...prev, modId]
    );
  };

  const handleApplyCustomPack = () => {
    if (customizingPack) {
      applyMethodology(customizingPack.id, customSelectedModules);
      setCustomizingPack(null);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }} className="animate-fade-in">
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
            <Badge variant="teal" size="sm">
              Modular Work Platform
            </Badge>
            <h1 style={{ fontSize: '24px', fontWeight: '800' }}>Forma Module & Methodology Library</h1>
          </div>
          <p style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>
            Tailor your workspace with specialized capability suites. Zero lock-in: toggle any module anytime without data loss.
          </p>
        </div>

        <Tabs tabs={libraryTabs} activeTab={activeTab} onChange={setActiveTab} />
      </div>

      {/* TAB 1: ALL MODULES */}
      {activeTab === 'modules' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {/* Search bar */}
          <div style={{ maxWidth: '360px' }}>
            <Input
              placeholder="Search module library..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              icon={<Search size={16} />}
            />
          </div>

          {/* Module Cards Grid */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
              gap: '18px'
            }}
          >
            {filteredModules.map((mod) => {
              const isEnabled = enabledModules.includes(mod.id);
              return (
                <Card key={mod.id} padding="22px">
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '14px' }}>
                    <div
                      style={{
                        width: '44px',
                        height: '44px',
                        borderRadius: 'var(--radius-md)',
                        backgroundColor: isEnabled ? 'rgba(35, 190, 179, 0.15)' : 'var(--surface-recessed)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <span
                        className="material-symbols-outlined"
                        style={{
                          fontSize: '24px',
                          color: isEnabled ? 'var(--accent-teal)' : 'var(--text-muted)'
                        }}
                      >
                        {mod.icon}
                      </span>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <Badge variant={isEnabled ? 'teal' : 'neutral'} size="sm">
                        {isEnabled ? 'Active' : 'Available'}
                      </Badge>
                    </div>
                  </div>

                  <h3 style={{ fontSize: '17px', fontWeight: '700', marginBottom: '4px' }}>
                    {mod.name}
                  </h3>
                  <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '8px', fontWeight: '600', textTransform: 'uppercase' }}>
                    {mod.category}
                  </div>

                  <p
                    style={{
                      fontSize: '13px',
                      color: 'var(--text-secondary)',
                      lineHeight: '1.5',
                      marginBottom: '20px',
                      minHeight: '40px'
                    }}
                  >
                    {mod.description}
                  </p>

                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      paddingTop: '14px',
                      borderTop: '1px solid var(--border-divider)',
                      marginTop: 'auto',
                      gap: '8px'
                    }}
                  >
                    {isEnabled ? (
                      <button
                        onClick={() => handleLaunchModule(mod.id)}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: 'var(--accent-teal)',
                          cursor: 'pointer',
                          fontSize: '12px',
                          fontWeight: '700',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px'
                        }}
                      >
                        <ExternalLink size={13} /> Open Module
                      </button>
                    ) : (
                      <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                        Hidden from sidebar
                      </span>
                    )}

                    <Button
                      variant={isEnabled ? 'recessed' : 'primary'}
                      size="sm"
                      onClick={() => toggleModule(mod.id)}
                      icon={isEnabled ? <Check size={14} /> : <Plus size={14} />}
                    >
                      {isEnabled ? 'Enabled' : 'Enable Module'}
                    </Button>
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {/* TAB 2: METHODOLOGY PACKS */}
      {activeTab === 'methodologies' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {/* Zero Lock-in Banner */}
          <div
            style={{
              padding: '16px 20px',
              backgroundColor: 'rgba(35, 190, 179, 0.08)',
              border: '1px solid rgba(35, 190, 179, 0.3)',
              borderRadius: 'var(--radius-md)',
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}
          >
            <ShieldCheck size={22} color="var(--accent-teal)" />
            <div style={{ fontSize: '13px', color: 'var(--text-primary)' }}>
              <strong>Zero Lock-in Guarantee:</strong> Applying a methodology pack recommends and enables the ideal
              modular suite without altering your data or locking your workspace. You can customize individual modules at any time.
            </div>
          </div>

          {/* Category Filter Pills */}
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
            {['all', 'Engineering', 'Interactive Media', 'Creative', 'Growth', 'Academia', 'Consulting', 'Personal', 'Custom'].map((cat) => (
              <button
                key={cat}
                onClick={() => setMethodologyCategory(cat)}
                style={{
                  padding: '6px 12px',
                  borderRadius: 'var(--radius-pill)',
                  backgroundColor: methodologyCategory === cat ? 'var(--accent-teal)' : 'var(--surface-recessed)',
                  color: methodologyCategory === cat ? 'var(--text-on-accent)' : 'var(--text-secondary)',
                  border: '1px solid var(--border-subtle)',
                  fontSize: '12px',
                  fontWeight: '700',
                  cursor: 'pointer',
                  transition: 'all 0.12s'
                }}
              >
                {cat === 'all' ? 'All Disciplines' : cat}
              </button>
            ))}
          </div>

          {/* Methodology Packs Grid */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
              gap: '18px'
            }}
          >
            {filteredMethodologies.map((pack) => {
              const isApplied = activeMethodology === pack.id;
              return (
                <Card key={pack.id} padding="22px">
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                    <span
                      className="material-symbols-outlined"
                      style={{ fontSize: '28px', color: 'var(--accent-teal)' }}
                    >
                      {pack.icon}
                    </span>
                    <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                      <Badge variant="recessed" size="xs">{pack.category}</Badge>
                      {isApplied && <Badge variant="teal" size="xs">Active</Badge>}
                    </div>
                  </div>

                  <h3 style={{ fontSize: '17px', fontWeight: '700', marginBottom: '6px' }}>
                    {pack.name}
                  </h3>
                  <p style={{ fontSize: '13px', color: 'var(--text-secondary)', lineHeight: '1.5', marginBottom: '14px', minHeight: '40px' }}>
                    {pack.description}
                  </p>

                  <div style={{ marginBottom: '18px' }}>
                    <div style={{ fontSize: '11px', fontWeight: '700', color: 'var(--text-muted)', marginBottom: '6px', textTransform: 'uppercase' }}>
                      Recommended Modules:
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                      {pack.recommendedModules.map((modId) => {
                        const m = MODULE_CATALOGUE.find((x) => x.id === modId);
                        const isModActive = enabledModules.includes(modId);
                        return (
                          <Badge
                            key={modId}
                            size="xs"
                            variant={isModActive ? 'teal' : 'recessed'}
                          >
                            {m?.name || modId}
                          </Badge>
                        );
                      })}
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: '8px' }}>
                    <Button
                      variant={isApplied ? 'recessed' : 'primary'}
                      size="sm"
                      fullWidth
                      onClick={() => applyMethodology(pack.id)}
                      icon={<Sparkles size={14} />}
                    >
                      {isApplied ? 'Re-Apply Preset' : 'Apply Preset'}
                    </Button>

                    <Button
                      variant="secondary"
                      size="sm"
                      icon={<Sliders size={14} />}
                      onClick={() => handleOpenCustomize(pack)}
                      title="Customize modules before applying"
                    >
                      Customize
                    </Button>
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {/* METHODOLOGY CUSTOMIZER MODAL */}
      {customizingPack && (
        <Modal
          isOpen={!!customizingPack}
          onClose={() => setCustomizingPack(null)}
          title={`Customize ${customizingPack.name}`}
          subtitle="Select the specific modules to activate for this discipline"
          footer={
            <>
              <Button variant="ghost" onClick={() => setCustomizingPack(null)}>
                Cancel
              </Button>
              <Button variant="primary" onClick={handleApplyCustomPack} icon={<Sparkles size={15} />}>
                Apply Custom Suite ({customSelectedModules.length} Modules)
              </Button>
            </>
          }
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '4px' }}>
              Check or uncheck modules to tailor the preset to your team's specific workflow:
            </p>

            {MODULE_CATALOGUE.map((mod) => {
              const isChecked = customSelectedModules.includes(mod.id);
              const isRecommended = customizingPack.recommendedModules.includes(mod.id);

              return (
                <label
                  key={mod.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '12px 14px',
                    backgroundColor: isChecked ? 'rgba(35, 190, 179, 0.08)' : 'var(--surface-recessed)',
                    borderRadius: 'var(--radius-md)',
                    border: isChecked ? '1px solid var(--accent-teal)' : '1px solid var(--border-subtle)',
                    cursor: 'pointer'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span className="material-symbols-outlined" style={{ fontSize: '20px', color: isChecked ? 'var(--accent-teal)' : 'var(--text-muted)' }}>
                      {mod.icon}
                    </span>
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ fontSize: '13px', fontWeight: '700', color: 'var(--text-primary)' }}>
                          {mod.name}
                        </span>
                        {isRecommended && <Badge variant="recessed" size="xs">Recommended</Badge>}
                      </div>
                      <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                        {mod.description}
                      </div>
                    </div>
                  </div>

                  <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={() => handleToggleCustomModule(mod.id)}
                    style={{ accentColor: 'var(--accent-teal)', cursor: 'pointer', transform: 'scale(1.1)' }}
                  />
                </label>
              );
            })}
          </div>
        </Modal>
      )}
    </div>
  );
};
